
import { DatePicker } from 'v-calendar';
import moment from 'moment';
import dropdownIcon from '~/assets/icons/dropdown-icon.png';

const InputControl = {
  name: 'LocaleDateTimeInputControl',
  inheritAttrs: false,
  props: {
    value: { default: '' },
    type: { type: String, default: 'date' },
    inputData: { default: () => ({}) },
  },
  data() {
    return { draft: '', editing: false, invalid: false };
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (!this.editing) {
          this.draft = this.displayValue(value);
          this.invalid = this.parseValue(value) === null;
        }
      },
    },
  },
  methods: {
    parseValue(value) {
      const text = String(value || '').trim();
      if (!text) return '';
      const formats = this.type === 'time'
        ? ['H:mm', 'HH:mm', 'Hmm', 'HHmm', 'H', 'HH', 'HH:mm:ss']
        : ['DD-MM-YYYY', 'D-M-YYYY', 'DDMMYYYY', 'YYYY-MM-DD'];
      const parsed = moment(text, formats, true);
      if (!parsed.isValid() || (this.type === 'time' && parsed.format('HH') === '00' && /^24/.test(text))) {
        return null;
      }
      return parsed.format(this.type === 'time' ? 'HH:mm' : 'YYYY-MM-DD');
    },
    displayValue(value) {
      if (!value) return '';
      const normalized = this.parseValue(value);
      if (normalized === null) return String(value);
      return this.type === 'time' ? normalized : moment(normalized, 'YYYY-MM-DD', true).format('DD-MM-YYYY');
    },
    commit() {
      const normalized = this.parseValue(this.draft);
      this.invalid = normalized === null;
      const value = normalized === null ? this.draft.trim() : normalized;
      if (!this.invalid) this.draft = this.displayValue(value);
      this.$emit('input', value);
      this.$emit('change', { target: { value } });
      return !this.invalid;
    },
    onPickerInput(value) {
      this.editing = false;
      this.draft = this.displayValue(value);
      this.commit();
    },
    onFocus(event) {
      this.editing = true;
      this.$emit('focus', event);
    },
    onInput(event) {
      this.editing = true;
      this.invalid = false;
      this.draft = event.target.value;
    },
    onBlur(event) {
      if (event.relatedTarget && this.$el.contains(event.relatedTarget)) return;
      this.editing = false;
      this.commit();
      this.$refs.picker?.hidePopover();
      this.$nextTick(() => this.$emit('blur', event));
    },
    onKeydown(event) {
      if (event.key === 'Tab' && this.type === 'time') {
        this.$el.querySelectorAll('.vc-popover-content-wrapper').forEach(popover => {
          popover.inert = true;
        });
        this.$refs.picker?.hidePopover({ hideDelay: 0 });
      } else if (event.key === 'Enter') {
        event.preventDefault();
        this.commit();
        this.$refs.picker?.hidePopover();
        this.$nextTick(() => this.$emit('blur', event));
      } else if (event.key === 'ArrowDown' && event.altKey) {
        event.preventDefault();
        this.enablePickerFocus();
        this.$refs.picker?.showPopover();
      } else if (event.key === 'Escape') {
        this.draft = this.displayValue(this.value);
        this.invalid = false;
      }
      this.$emit('keydown', event);
    },
    enablePickerFocus() {
      this.$el.querySelectorAll('.vc-popover-content-wrapper').forEach(popover => {
        popover.inert = false;
      });
    },
  },
  render(createElement) {
    const data = this.inputData;
    const isTime = this.type === 'time';
    const attrs = { ...data.attrs };
    const disabled = attrs.disabled != null && attrs.disabled !== false;
    const timeHint = isTime && String(attrs.placeholder || '').match(/^(\d{1,2}):(\d{2})$/);
    const suggestedTime = timeHint && Number(timeHint[1]) < 24 && Number(timeHint[2]) < 60
      ? `${timeHint[1].padStart(2, '0')}:${timeHint[2]}`
      : null;

    return createElement(DatePicker, {
      ref: 'picker',
      class: { 'locale-time-only': isTime },
      props: {
        value: this.parseValue(this.value) || null,
        mode: isTime ? 'time' : 'date',
        locale: 'nl',
        is24hr: true,
        modelConfig: { type: 'string', mask: isTime ? 'HH:mm' : 'YYYY-MM-DD' },
        masks: { input: 'DD-MM-YYYY', inputTime24hr: 'HH:mm' },
        attributes: isTime ? [] : [{
          key: 'today',
          dates: new Date(),
          highlight: { color: 'red', fillMode: 'outline' },
        }],
        popover: { visibility: disabled || isTime ? 'hidden' : 'focus', placement: 'bottom-start' },
      },
      style: { width: '100%' },
      on: {
        input: this.onPickerInput,
      },
      nativeOn: {
        focusout: event => {
          if (event.target !== this.$refs.input && !(event.relatedTarget && this.$el.contains(event.relatedTarget))) {
            this.onBlur(event);
          }
        },
      },
      scopedSlots: {
        footer: suggestedTime && !this.value && !disabled
          ? () => createElement('button', {
            attrs: { type: 'button' },
            class: 'locale-time-confirm',
            on: {
              click: () => this.onPickerInput(suggestedTime),
            },
          }, 'Toepassen')
          : undefined,
        default: ({ inputEvents, togglePopover }) => {
          const events = isTime ? { ...this.$listeners } : { ...this.$listeners, ...inputEvents };
          events.input = this.onInput;
          events.change = () => {};
          events.blur = this.onBlur;
          events.keydown = this.onKeydown;
          const pickerFocus = inputEvents.focus;
          events.focus = event => {
            this.onFocus(event);
            if (isTime && !disabled) {
              this.enablePickerFocus();
              this.$refs.picker?.showPopover();
            } else if (pickerFocus) {
              pickerFocus(event);
            }
          };
          const input = createElement('input', {
            ref: 'input',
            attrs: {
              ...attrs,
              type: 'text',
              lang: 'nl',
              placeholder: isTime ? (attrs.placeholder || 'hh:mm') : 'dd-mm-yyyy',
              inputmode: 'numeric',
              autocomplete: 'off',
              disabled,
              'aria-invalid': this.invalid ? 'true' : undefined,
            },
            staticClass: data.staticClass,
            class: [data.class, { 'locale-time-text': isTime, 'border-2 border-darkred': this.invalid }],
            style: data.style,
            domProps: { value: this.draft },
            on: events,
          });
          if (!isTime) return input;
          return createElement('div', { class: 'locale-time-field' }, [
            input,
            createElement('button', {
              attrs: { type: 'button', title: 'Tijd kiezen', 'aria-label': 'Tijd kiezen', tabindex: '-1', disabled },
              class: 'locale-time-toggle',
              on: { click: () => { if (!disabled) { this.enablePickerFocus(); togglePopover(); } } },
            }, [createElement('img', { attrs: { src: dropdownIcon, alt: '' } })]),
          ]);
        },
      },
    });
  },
};

export default {
  name: 'LocaleDateTimeInput',
  functional: true,
  props: {
    value: { default: '' },
    type: { type: String, default: 'date' },
  },
  render(createElement, { props, data, listeners }) {
    return createElement(InputControl, {
      props: { ...props, inputData: data },
      on: listeners,
    });
  },
};
